import React, { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { isOfflineMode, formatDay, formatTime, venueText } from './AssignMentorDialog';
import { experienceDisplay, STATUS_DOTS, todayInSchoolTz } from '@/lib/experienceStatus';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TYPE_FILTERS = [
  { id: 'all', label: 'All experience types' },
  { id: 'online', label: 'Online Sessions' },
  { id: 'offline', label: 'Workplace Visits' },
];
const STATUS_FILTERS = [
  { id: 'scheduled', label: 'Upcoming & scheduled' },
  { id: 'all', label: 'All statuses' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'pending_parent_confirmation', label: 'Awaiting parent' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'not_conducted', label: 'Not conducted' },
];
const LEGEND = ['confirmed', 'awaiting_completion', 'completed', 'not_conducted', 'closed'];
const LEGEND_LABELS = {
  confirmed: 'Confirmed', awaiting_completion: 'Awaiting completion',
  completed: 'Completed', not_conducted: 'Not conducted', closed: 'Closed',
};

/**
 * One school-wide calendar across experience types. Grouped sessions appear once
 * with their participant count; the type filter already carries the future
 * experience types so they can be switched on without reshaping this view.
 */
export default function SchoolCalendar({ bookings, onRefresh, onOpenDetails }) {
  const [month, setMonth] = useState(new Date());
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('scheduled');
  const [openDay, setOpenDay] = useState(null);

  const year = month.getFullYear(), mo = month.getMonth();
  const todayStr = todayInSchoolTz();

  // Collapse bookings into events: one per grouped session, one per unscheduled booking
  const events = useMemo(() => {
    const map = new Map();
    bookings.forEach(b => {
      if (!b.assigned_date) return;
      const offline = isOfflineMode(b.assigned_mode || b.requested_mode);
      if (type === 'online' && offline) return;
      if (type === 'offline' && !offline) return;
      if (status === 'scheduled') {
        if (!['confirmed', 'pending_parent_confirmation', 'awaiting_reschedule'].includes(b.status)) return;
        if (b.assigned_date < todayStr) return;
      } else if (status === 'completed') {
        if (!['completed', 'awaiting_feedback'].includes(b.status)) return;
      } else if (status === 'cancelled') {
        if (!['cancelled', 'rejected'].includes(b.status)) return;
      } else if (status !== 'all' && b.status !== status) return;
      const key = b.session_id || `booking-${b.id}`;
      const existing = map.get(key);
      if (existing) {
        existing.bookings.push(b);
        existing.participants += 1;
      } else {
        map.set(key, {
          key, date: b.assigned_date, time: b.assigned_time_slot, end_time: b.assigned_end_time,
          offline, career_name: b.career_name, mentor_name: b.mentor_name,
          venue: b.assigned_venue, status: b.status, session_id: b.session_id,
          meet_link: b.google_meet_link, duration: b.assigned_duration_minutes,
          participants: 1, bookings: [b],
          // Lifecycle fields, so the shared status helper reads an event like a booking
          assigned_date: b.assigned_date, assigned_time_slot: b.assigned_time_slot,
          assigned_end_time: b.assigned_end_time,
          assigned_duration_minutes: b.assigned_duration_minutes,
          admin_closed_at: b.admin_closed_at,
        });
      }
    });
    const byDate = {};
    [...map.values()].forEach(ev => {
      (byDate[ev.date] = byDate[ev.date] || []).push(ev);
    });
    Object.values(byDate).forEach(list => list.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return byDate;
  }, [bookings, type, status, todayStr]);

  const cells = [];
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthCount = Object.entries(events)
    .filter(([d]) => d.startsWith(`${year}-${String(mo + 1).padStart(2, '0')}`))
    .reduce((n, [, list]) => n + list.length, 0);

  return (
    <div data-testid="school-calendar">
      {/* Title + month navigation, then the two filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <h2 className="hidden sm:block text-base font-bold text-[#1F2937] font-['Outfit'] tracking-tight mr-1" data-testid="cal-title">School Calendar</h2>
          <button onClick={() => setMonth(new Date(year, mo - 1, 1))} className="h-11 w-11 sm:h-auto sm:w-auto sm:p-1.5 flex items-center justify-center rounded-lg hover:bg-[#F3F4F6]" data-testid="cal-prev" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <div>
            <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']" data-testid="cal-month">{month.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
            <p className="text-[11px] text-[#9CA3AF]" data-testid="cal-count">{monthCount} experience{monthCount === 1 ? '' : 's'} this month</p>
          </div>
          <button onClick={() => setMonth(new Date(year, mo + 1, 1))} className="h-11 w-11 sm:h-auto sm:w-auto sm:p-1.5 flex items-center justify-center rounded-lg hover:bg-[#F3F4F6]" data-testid="cal-next" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 w-[170px] rounded-lg border-gray-200 bg-white text-xs" data-testid="cal-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTERS.map(t => (
                <SelectItem key={t.id} value={t.id} className="text-xs" data-testid={`cal-type-${t.id}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-[180px] rounded-lg border-gray-200 bg-white text-xs" data-testid="cal-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-xs" data-testid={`cal-status-${s.id}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3" data-testid="cal-legend">
        {LEGEND.map(s => (
          <span key={s} className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
            <span className={`h-2 w-2 rounded-full ${STATUS_DOTS[s]}`} />{LEGEND_LABELS[s]}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] text-[#9CA3AF]"><Users className="h-3 w-3" />×n = students in a grouped session</span>
      </div>

      <div className="hidden sm:grid grid-cols-7 text-center text-[10px] font-semibold text-[#9CA3AF] mb-1">
        {DAYS.map(d => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="hidden sm:grid grid-cols-7 gap-px bg-[#E5E7EB] rounded-xl overflow-hidden">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="bg-[#F8F9FA] min-h-[84px]" />;
          const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const dayEvents = events[dateStr] || [];
          const isToday = dateStr === todayStr;
          return (
            <button key={i} onClick={() => dayEvents.length && setOpenDay({ date: dateStr, events: dayEvents })}
              className={`bg-white min-h-[84px] p-1 text-left align-top hover:bg-[#FAFBFC] transition-colors ${isToday ? 'ring-1 ring-[#F97D6A] ring-inset' : ''}`}
              data-testid={`cal-day-${dateStr}`}>
              <p className={`text-[10px] font-medium mb-0.5 ${isToday ? 'text-[#F97D6A] font-bold' : 'text-[#6B7280]'}`}>{d}</p>
              {dayEvents.slice(0, 2).map(ev => (
                <span key={ev.key} onClick={(e) => { e.stopPropagation(); onOpenDetails(ev.bookings[0]); }}
                  className={`flex items-center gap-1 text-[9px] px-1 py-0.5 rounded mb-0.5 truncate cursor-pointer ${experienceDisplay(ev).tone}`}
                  data-testid={`cal-event-${ev.key}`}>
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_DOTS[experienceDisplay(ev).key] || 'bg-gray-400'}`} />
                  <span className="truncate">{ev.time ? `${formatTime(ev.time)} ` : ''}{ev.career_name}</span>
                  {ev.participants > 1 && <span className="font-bold shrink-0">×{ev.participants}</span>}
                </span>
              ))}
              {dayEvents.length > 2 && <p className="text-[9px] text-[#9CA3AF]">+{dayEvents.length - 2} more</p>}
            </button>
          );
        })}
      </div>

      {/* Phone view: the same month as a readable agenda, with the same month navigation,
          filters and booking details behind each entry */}
      <div className="sm:hidden space-y-2" data-testid="calendar-agenda">
        {(() => {
          const monthPrefix = `${year}-${String(mo + 1).padStart(2, '0')}`;
          const days = Object.entries(events)
            .filter(([d]) => d.startsWith(monthPrefix))
            .sort(([a], [b]) => a.localeCompare(b));
          if (!days.length) {
            return (
              <p className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-6 text-sm text-[#9CA3AF] text-center"
                 data-testid="calendar-agenda-empty">
                Nothing scheduled in {month.toLocaleString('default', { month: 'long', year: 'numeric' })}.
              </p>
            );
          }
          return days.map(([date, list]) => (
            <div key={date} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden"
                 data-testid={`agenda-day-${date}`}>
              <div className={`px-3 py-2 text-xs font-semibold border-b border-[#F3F4F6] ${date === todayStr ? 'bg-[#FFF0ED] text-[#F06651]' : 'bg-[#F8F9FA] text-[#4B5563]'}`}>
                {formatDay(date)}{date === todayStr ? ' · Today' : ''}
              </div>
              {list.map(ev => (
                <button key={ev.key} onClick={() => onOpenDetails(ev.bookings[0])}
                  className="w-full text-left px-3 py-3 border-b border-[#F7F8F9] last:border-0 active:bg-[#FAFBFC]"
                  data-testid={`agenda-event-${ev.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-[#1F2937] break-words">{ev.career_name}</p>
                    <Badge className={`border-0 text-[10px] shrink-0 ${experienceDisplay(ev).tone}`}>{experienceDisplay(ev).label}</Badge>
                  </div>
                  <p className="text-[11px] text-[#6B7280] mt-0.5 break-words">
                    {ev.time ? formatTime(ev.time) : 'Time to confirm'}
                    {ev.mentor_name ? ` · ${ev.mentor_name}` : ''}
                    {` · ${ev.participants} student${ev.participants === 1 ? '' : 's'}`}
                    {ev.offline && venueText(ev.venue) ? ` · ${venueText(ev.venue)}` : ''}
                  </p>
                </button>
              ))}
            </div>
          ));
        })()}
      </div>

      {/* Day drawer */}
      <Dialog open={!!openDay} onOpenChange={() => setOpenDay(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="cal-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{openDay && formatDay(openDay.date)}</DialogTitle>
            <DialogDescription>{openDay?.events.length} experience{openDay?.events.length === 1 ? '' : 's'} scheduled</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {openDay?.events.map(ev => (
              <button key={ev.key} onClick={() => { setOpenDay(null); onOpenDetails(ev.bookings[0]); }}
                className="w-full text-left bg-[#F8F9FA] hover:bg-[#F3F4F6] rounded-xl px-3 py-2.5 transition-colors" data-testid={`cal-day-event-${ev.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-[#1F2937]">{ev.career_name}</p>
                  <Badge className={`border-0 text-[10px] ${experienceDisplay(ev).tone}`}>{experienceDisplay(ev).label}</Badge>
                </div>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  {ev.time ? formatTime(ev.time) : 'Time to confirm'}{ev.mentor_name ? ` · ${ev.mentor_name}` : ''} · {ev.participants} student{ev.participants === 1 ? '' : 's'}
                </p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
